<script setup>
import { VForm } from 'vuetify/components/VForm'
import { themeConfig } from '@themeConfig'
import { useClientAccountStore } from '@/stores/clientAccount'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
    clientArea: true,
  },
})

const accountStore = useClientAccountStore()
const { t } = useI18n()
const email = ref('')
const formError = ref('')
const successMessage = ref('')
const refVForm = ref()
const loading = ref(false)

const submit = async () => {
  formError.value = ''
  successMessage.value = ''
  loading.value = true
  try {
    const result = await accountStore.requestPasswordReset(email.value)

    successMessage.value = result.message
  }
  catch (err) {
    formError.value = err.message || t('clientForgotPassword.genericError')
  }
  finally {
    loading.value = false
  }
}

const onSubmit = () => {
  refVForm.value?.validate().then(({ valid }) => {
    if (valid)
      submit()
  })
}
</script>

<template>
  <div class="auth-wrapper d-flex align-center justify-center pa-4">
    <VCard
      flat
      border
      max-width="480"
      width="100%"
      class="pa-2"
    >
      <VCardText>
        <div class="d-flex align-center gap-x-3 mb-6">
          <VNodeRenderer :nodes="themeConfig.app.logo" />
          <span class="text-h5 text-high-emphasis">FitX</span>
        </div>

        <h1 class="text-h4 mb-1">
          {{ $t('clientForgotPassword.title') }}
        </h1>
        <p class="mb-0 text-medium-emphasis">
          {{ $t('clientForgotPassword.subtitle') }}
        </p>
      </VCardText>

      <VCardText>
        <VAlert
          v-if="formError"
          type="error"
          variant="tonal"
          class="mb-4"
          role="alert"
        >
          {{ formError }}
        </VAlert>

        <VAlert
          v-if="successMessage"
          type="success"
          variant="tonal"
          class="mb-4"
          role="status"
        >
          {{ successMessage }}
        </VAlert>

        <VForm
          ref="refVForm"
          @submit.prevent="onSubmit"
        >
          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="email"
                :label="$t('common.email')"
                type="email"
                autocomplete="username"
                autofocus
                :rules="[requiredValidator, emailValidator]"
                :disabled="loading"
              />
            </VCol>

            <VCol cols="12">
              <VBtn
                block
                type="submit"
                color="primary"
                :loading="loading"
              >
                {{ $t('clientForgotPassword.submit') }}
              </VBtn>
            </VCol>

            <VCol
              cols="12"
              class="text-center"
            >
              <RouterLink
                class="text-primary"
                to="/client/login"
              >
                {{ $t('common.backToSignIn') }}
              </RouterLink>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>
    </VCard>
  </div>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth";
</style>
